import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { FaPaperclip, FaTimes } from "react-icons/fa";
import { useTranslation } from 'react-i18next';
import i18n from '../../../../i18n/i18n';
import {
  addOpeningBalance,
  getPostingAccounts,
  clearError,
  clearSuccess,
} from "../../../../redux/Slices/authSlice";

const emptyDetail = () => ({ account_id: "", debit: "", credit: "" });


// add opening balance component
const AddOpeningBalance = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { postingAccounts, isLoading, error, success } = useSelector(
    (state) => state.auth
  );

  const lastErrorRef = useRef({ message: null, time: 0 });
  const { t, i18n: i18nHook } = useTranslation('global');
  const isRTL = i18nHook.language === 'ar';
  const dir = isRTL ? 'rtl' : 'ltr';
  const textAlign = isRTL ? 'right' : 'left';

  const [windowWidth, setWindowWidth] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth : 1200
  );
  const [form, setForm] = useState({
    date: "",
    description: "",
    details: [emptyDetail()],
  });
  const [attachments, setAttachments] = useState([]);
  const [searchTerms, setSearchTerms] = useState({});
  const [openDropdowns, setOpenDropdowns] = useState({});
  const searchInputRefs = useRef({});

  useEffect(() => {
    dispatch(getPostingAccounts());
  }, [dispatch]);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(typeof window !== "undefined" ? window.innerWidth : 1200);
    };
    if (typeof window !== "undefined") {
      window.addEventListener("resize", handleResize);
    }
    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("resize", handleResize);
      }
    };
  }, []);

  useEffect(() => {
    if (success) {
      toast.success(success, { rtl: isRTL });
      dispatch(clearSuccess());
      navigate("/opening-balance");
    }
  }, [success, dispatch, navigate, isRTL]);

  useEffect(() => {
    if (error) {
      const now = Date.now();
      const last = lastErrorRef.current;
      if (!last.message || last.message !== error || now - last.time > 2000) {
        toast.error(error, { rtl: isRTL });
        lastErrorRef.current = { message: error, time: now };
      }
      setTimeout(() => dispatch(clearError()), 3000);
    }
  }, [error, dispatch, isRTL]);

  const isMobile = windowWidth < 768;
  const isTablet = windowWidth >= 768 && windowWidth < 1024;

  const updateDetail = (idx, field, value) => {
    setForm((prev) => ({
      ...prev,
      details: prev.details.map((detail, index) =>
        index === idx ? { ...detail, [field]: value } : detail
      ),
    }));
  };

  const addDetailRow = () => {
    setForm((prev) => ({
      ...prev,
      details: [...prev.details, emptyDetail()],
    }));
  };

  const removeDetailRow = (idx) => {
    setForm((prev) => ({
      ...prev,
      details:
        prev.details.length > 1
          ? prev.details.filter((_, i) => i !== idx)
          : prev.details,
    }));

    setOpenDropdowns((prev) => {
      const updated = { ...prev };
      delete updated[idx];
      return updated;
    });
    setSearchTerms((prev) => {
      const updated = { ...prev };
      delete updated[idx];
      return updated;
    });
  };

  const handleAttachmentChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const mapped = files.map((file) => ({
      id: Date.now() + Math.random(),
      file,
      name: file.name,
      size: file.size,
    }));

    setAttachments((prev) => [...prev, ...mapped]);
    e.target.value = "";
  };

  const removeAttachment = (id) => {
    setAttachments((prev) => prev.filter((att) => att.id !== id));
  };

  const totals = useMemo(() => {
    const debit = form.details.reduce(
      (sum, detail) => sum + (parseFloat(detail.debit) || 0),
      0
    );
    const credit = form.details.reduce(
      (sum, detail) => sum + (parseFloat(detail.credit) || 0),
      0
    );
    const difference = debit - credit;
    return {
      totalDebit: debit,
      totalCredit: credit,
      difference,
    };
  }, [form.details]);

  const differenceColor = totals.difference === 0 ? "#22c55e" : "#f97316";

  const buildPayload = (detailsArray) => {
    const base = {
      date: form.date,
      description: form.description,
      details: JSON.stringify(detailsArray),
    };

    if (attachments.length > 0) {
      const fd = new FormData();
      fd.append("date", base.date);
      fd.append("description", base.description || "");
      fd.append("details", base.details);
      attachments.forEach((attachment, index) => {
        fd.append(`attachments[${index}]`, attachment.file);
      });
      return fd;
    }

    return base;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.date) {
      toast.warn(t('openingBalances.form.dateRequired'), { rtl: isRTL });
      return;
    }

    const detailsArray = form.details
      .map((detail) => ({
        account_id: Number(detail.account_id),
        debit: parseFloat(detail.debit) || 0,
        credit: parseFloat(detail.credit) || 0,
      }))
      .filter(
        (detail) =>
          detail.account_id &&
          (Number(detail.debit) > 0 || Number(detail.credit) > 0)
      );

    if (detailsArray.length === 0) {
      toast.warn(t('openingBalances.form.detailsRequired'), {
        rtl: isRTL,
      });
      return;
    }

    const payload = buildPayload(detailsArray);
    dispatch(addOpeningBalance(payload));
  };

  return (
    <div
      style={{
        padding: "30px",
        backgroundColor: "#14263b",
        minHeight: "calc(100vh - 80px)",
        color: "white",
        direction: dir
      }}
    >
      <div
        style={{
          marginBottom: "20px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: isMobile ? "stretch" : "center",
          gap: "12px",
          flexDirection: isMobile ? "column" : "row",
        }}
      >
        <div>
          <h1 style={{ fontSize: "20px", margin: 0, textAlign }}>{t('openingBalances.form.title')}</h1>
          <p style={{ margin: "6px 0 0", color: "#9ca3af", fontSize: "13px", textAlign }}>
            {t('openingBalances.form.description')}
          </p>
        </div>
        <button
          onClick={() => navigate("/opening-balance")}
          style={{
            backgroundColor: "#374151",
            border: "none",
            color: "white",
            padding: "10px 16px",
            borderRadius: "8px",
            cursor: "pointer",
            fontSize: "14px",
          }}
        >
          {t('openingBalances.form.backToList')}
        </button>
      </div>

      <form
        onSubmit={handleSubmit}
        style={{
          width: "100%",
          maxWidth: "1280px",
          backgroundColor: "#1f2937",
          border: "1px solid #333",
          borderRadius: "12px",
          padding: isMobile ? "16px" : "24px",
          display: "flex",
          flexDirection: "column",
          gap: "20px",
          pointerEvents: (isLoading || success) ? 'none' : 'auto',
          opacity: (isLoading || success) ? 0.7 : 1
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isTablet || isMobile ? "1fr" : "1fr 1fr",
            gap: "12px",
            marginBottom: "16px",
          }}
        >
          <div>
            <label style={{ ...labelStyle, textAlign }}>{t('openingBalances.form.date')}</label>
            <input
              type="date"
              value={form.date}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, date: e.target.value }))
              }
              required
              style={inputStyle}
            />
          </div>
          <div>
            <label style={{ ...labelStyle, textAlign }}>{t('openingBalances.form.descriptionLabel')}</label>
            <input
              type="text"
              value={form.description}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, description: e.target.value }))
              }
              style={inputStyle}
            />
          </div>
        </div>

        <div style={{ marginBottom: "18px" }}>
          <label style={{ ...labelStyle, marginBottom: "8px", textAlign }}>
            {t('openingBalances.form.details')}
          </label>
          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            {form.details.map((detail, idx) => (
              <div
                key={idx}
                style={{
                  display: "grid",
                  gridTemplateColumns: isMobile
                    ? "1fr"
                    : "minmax(0, 3fr) minmax(0, 2fr) minmax(0, 2fr) auto",
                  gap: "12px",
                  padding: "12px",
                  border: "1px solid #333",
                  borderRadius: "10px",
                  backgroundColor: "#1a1f2e",
                }}
              >
                <div style={{ position: "relative" }}>
                  <label style={{ ...labelStyle, textAlign }}>{t('openingBalances.form.account')}</label>
                  <div
                    onClick={() => {
                      const willOpen = !openDropdowns[idx];
                      setOpenDropdowns((prev) => ({ ...prev, [idx]: willOpen }));
                      if (willOpen) {
                        setTimeout(() => {
                          if (searchInputRefs.current[idx]) {
                            searchInputRefs.current[idx].focus();
                          }
                        }, 100);
                      }
                    }}
                    style={{
                      width: "100%",
                      padding: "10px",
                      backgroundColor: "#1a1f2e",
                      border: "1px solid #333",
                      borderRadius: "8px",
                      color: "white",
                      cursor: "pointer",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      boxSizing: "border-box",
                    }}
                  >
                    <span>
                      {detail.account_id
                        ? (() => {
                          const selected = postingAccounts?.find(
                            (acc) => String(acc.id) === String(detail.account_id)
                          );
                          if (!selected) return t('openingBalances.form.selectAccount');
                          const accountName = isRTL ? (selected.name_ar || selected.name) : (selected.name_en || selected.name || selected.name_ar);
                          return `${selected.code} - ${accountName}`;
                        })()
                        : t('openingBalances.form.selectAccount')}
                    </span>
                    <span style={{ fontSize: "12px" }}>▼</span>
                  </div>
                  {openDropdowns[idx] && (
                    <>
                      <div
                        style={{
                          position: "absolute",
                          top: "100%",
                          left: 0,
                          right: 0,
                          backgroundColor: "#1a1f2e",
                          border: "1px solid #333",
                          borderRadius: "0 0 8px 8px",
                          zIndex: 1000,
                          maxHeight: "300px",
                          overflowY: "auto",
                          boxShadow: "0 4px 6px rgba(0,0,0,0.3)",
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          ref={(el) => (searchInputRefs.current[idx] = el)}
                          type="text"
                          placeholder={t('openingBalances.form.searchAccount')}
                          value={searchTerms[idx] || ""}
                          onChange={(e) => {
                            const searchValue = e.target.value;
                            setSearchTerms((prev) => ({ ...prev, [idx]: searchValue }));
                          }}
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            width: "100%",
                            padding: "10px",
                            backgroundColor: "#2d3748",
                            border: "none",
                            borderBottom: "1px solid #333",
                            color: "white",
                            fontSize: "14px",
                            boxSizing: "border-box",
                          }}
                        />
                        <div style={{ maxHeight: "250px", overflowY: "auto" }}>
                          {(() => {
                            const searchTerm = (searchTerms[idx] || "").toLowerCase();
                            const filteredAccounts = (postingAccounts || []).filter(
                              (acc) => {
                                const code = acc.code?.toLowerCase() || "";
                                const name = isRTL
                                  ? (acc.name_ar?.toLowerCase() || acc.name?.toLowerCase() || "")
                                  : (acc.name_en?.toLowerCase() || acc.name?.toLowerCase() || acc.name_ar?.toLowerCase() || "");
                                if (!searchTerm) return true;
                                return (
                                  code.includes(searchTerm) || name.includes(searchTerm)
                                );
                              }
                            );

                            if (filteredAccounts.length === 0) {
                              return (
                                <div
                                  style={{
                                    padding: "10px",
                                    color: "#888",
                                    textAlign: "center",
                                  }}
                                >
                                  {t('openingBalances.form.noResults')}
                                </div>
                              );
                            }

                            return filteredAccounts.map((acc) => {
                              const isSelectedInOtherLine = form.details.some((d, i) => {
                                if (i === idx) return false;
                                return (
                                  d.account_id &&
                                  String(d.account_id) === String(acc.id)
                                );
                              });
                              const isSelected =
                                String(detail.account_id) === String(acc.id);
                              return (
                                <div
                                  key={acc.id}
                                  onClick={() => {
                                    if (!isSelectedInOtherLine) {
                                      updateDetail(idx, "account_id", acc.id);
                                      setOpenDropdowns((prev) => ({
                                        ...prev,
                                        [idx]: false,
                                      }));
                                      setSearchTerms((prev) => ({
                                        ...prev,
                                        [idx]: "",
                                      }));
                                    }
                                  }}
                                  style={{
                                    padding: "10px",
                                    cursor: isSelectedInOtherLine ? "not-allowed" : "pointer",
                                    backgroundColor: isSelected
                                      ? "#AC2000"
                                      : isSelectedInOtherLine
                                        ? "#2a2a2a"
                                        : "transparent",
                                    color: isSelectedInOtherLine ? "#888" : "white",
                                    borderBottom: "1px solid #333",
                                    opacity: isSelectedInOtherLine ? 0.5 : 1,
                                  }}
                                  onMouseEnter={(e) => {
                                    if (!isSelectedInOtherLine && !isSelected) {
                                      e.currentTarget.style.backgroundColor = "#374151";
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    if (!isSelectedInOtherLine && !isSelected) {
                                      e.currentTarget.style.backgroundColor = "transparent";
                                    }
                                  }}
                                >
                                  {acc.code} - {isRTL ? (acc.name_ar || acc.name) : (acc.name_en || acc.name || acc.name_ar)}{" "}
                                  {isSelectedInOtherLine ? `(${t('openingBalances.form.alreadySelected')})` : ""}
                                </div>
                              );
                            });
                          })()}
                        </div>
                      </div>
                      <div
                        style={{
                          position: "fixed",
                          inset: 0,
                          zIndex: 999,
                        }}
                        onClick={() =>
                          setOpenDropdowns((prev) => ({ ...prev, [idx]: false }))
                        }
                      />
                    </>
                  )}
                </div>
                <div>
                  <label style={{ ...labelStyle, textAlign }}>{t('openingBalances.form.debit')}</label>
                  <input
                    type="number"

                    value={detail.debit}
                    disabled={!!detail.credit && parseFloat(detail.credit) > 0}
                    onChange={(e) =>
                      updateDetail(idx, "debit", e.target.value)
                    }
                    onBlur={(e) => {
                      const raw = parseFloat(e.target.value || 0);
                      const normalized = Number.isFinite(raw)
                        ? raw.toFixed(2)
                        : "";
                      updateDetail(idx, "debit", normalized);
                    }}
                    style={{
                      ...inputStyle,
                      opacity: (!!detail.credit && parseFloat(detail.credit) > 0) ? 0.5 : 1,
                      cursor: (!!detail.credit && parseFloat(detail.credit) > 0) ? 'not-allowed' : 'text'
                    }}
                  />
                </div>
                <div>
                  <label style={{ ...labelStyle, textAlign }}>{t('openingBalances.form.credit')}</label>
                  <input
                    type="number"

                    value={detail.credit}
                    disabled={!!detail.debit && parseFloat(detail.debit) > 0}
                    onChange={(e) =>
                      updateDetail(idx, "credit", e.target.value)
                    }
                    onBlur={(e) => {
                      const raw = parseFloat(e.target.value || 0);
                      const normalized = Number.isFinite(raw)
                        ? raw.toFixed(2)
                        : "";
                      updateDetail(idx, "credit", normalized);
                    }}
                    style={{
                      ...inputStyle,
                      opacity: (!!detail.debit && parseFloat(detail.debit) > 0) ? 0.5 : 1,
                      cursor: (!!detail.debit && parseFloat(detail.debit) > 0) ? 'not-allowed' : 'text'
                    }}
                  />
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginTop: isMobile ? "0" : "24px",
                  }}
                >
                  <button
                    type="button"
                    onClick={() => removeDetailRow(idx)}
                    style={{
                      backgroundColor: "#dc2626",
                      color: "white",
                      border: "none",
                      borderRadius: "8px",
                      padding: "10px 14px",
                      cursor: "pointer",
                      fontSize: "13px",
                    }}
                  >
                    {t('openingBalances.form.delete')}
                  </button>
                </div>
              </div>
            ))}
            <button
              type="button"
              onClick={addDetailRow}
              style={{
                alignSelf: "flex-start",
                backgroundColor: "transparent",
                color: "#FFFFFF",
                border: "1px solid #AC2000",
                borderRadius: "8px",
                padding: "10px 16px",
                cursor: "pointer",
                fontSize: "13px",
              }}
            >
              {t('openingBalances.form.addRow')}
            </button>
            <div
              style={{
                marginTop: "12px",
                backgroundColor: "#1a1f2e",
                border: "1px solid #333",
                borderRadius: "10px",
                padding: "16px",
              }}
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isMobile ? "1fr" : "repeat(3, minmax(0, 1fr))",
                  gap: "16px",
                  textAlign: "center",
                }}
              >
                <div>
                  <div style={{ color: "#9ca3af", fontSize: "12px", marginBottom: "4px" }}>
                    {t('openingBalances.form.totalDebit')}
                  </div>
                  <div style={{ fontSize: "18px", fontWeight: "bold", color: "white" }}>
                    {totals.totalDebit.toFixed(2)}
                  </div>
                </div>
                <div>
                  <div style={{ color: "#9ca3af", fontSize: "12px", marginBottom: "4px" }}>
                    {t('openingBalances.form.totalCredit')}
                  </div>
                  <div style={{ fontSize: "18px", fontWeight: "bold", color: "white" }}>
                    {totals.totalCredit.toFixed(2)}
                  </div>
                </div>
                <div>
                  <div style={{ color: "#9ca3af", fontSize: "12px", marginBottom: "4px" }}>
                    {t('openingBalances.form.difference')}
                  </div>
                  <div style={{ fontSize: "18px", fontWeight: "bold", color: differenceColor }}>
                    {totals.difference.toFixed(2)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div style={{ marginTop: "24px", marginBottom: "24px" }}>
          <label
            style={{
              display: "block",
              marginBottom: "15px",
              fontWeight: "bold",
              color: "white",
              fontSize: "16px",
              textAlign: isRTL ? 'right' : 'left',
            }}
          >
            {t('openingBalances.form.attachments')}
          </label>

          <div style={{ marginBottom: "20px", display: "block", width: "100%" }}>
            <input
              type="file"
              id="openingBalanceAttachments"
              multiple
              accept="*/*"
              onChange={handleAttachmentChange}
              style={{ display: "none" }}
            />
            <label
              htmlFor="openingBalanceAttachments"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                backgroundColor: "transparent",
                color: "white",
                padding: "12px 20px",
                borderRadius: "8px",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: "bold",
                border: "2px solid #AC2000",
                transition: "all 0.3s ease",
                textAlign: isRTL ? 'right' : 'left',
                justifyContent: isRTL ? 'flex-end' : 'flex-start',
                direction: isRTL ? 'rtl' : 'ltr',
                width: "fit-content"
              }}
            >
              <FaPaperclip />
              {t('openingBalances.form.addAttachments')}
            </label>
          </div>

          {attachments.length > 0 && (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: `repeat(auto-fill, minmax(${isMobile ? 160 : 200}px, 1fr))`,
                gap: "15px",
                marginTop: "20px",
              }}
            >
              {attachments.map((attachment) => (
                <div
                  key={attachment.id}
                  style={{
                    backgroundColor: "#1a1f2e",
                    padding: "15px",
                    borderRadius: "8px",
                    border: "1px solid #333",
                    position: "relative",
                  }}
                >
                  <button
                    type="button"
                    onClick={() => removeAttachment(attachment.id)}
                    style={{
                      position: "absolute",
                      top: "5px",
                      left: "5px",
                      backgroundColor: "#dc3545",
                      color: "white",
                      border: "none",
                      borderRadius: "50%",
                      width: "25px",
                      height: "25px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "12px",
                    }}
                  >
                    <FaTimes />
                  </button>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <FaPaperclip style={{ fontSize: "24px", color: "#AC2000" }} />
                    <div
                      style={{
                        fontSize: "12px",
                        color: "white",
                        textAlign: "center",
                        wordBreak: "break-word",
                        maxWidth: "100%",
                      }}
                    >
                      {attachment.name}
                    </div>
                    <div style={{ fontSize: "10px", color: "#999" }}>
                      {(attachment.size / 1024).toFixed(2)} KB
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div
          style={{
            marginTop: "24px",
            display: "flex",
            gap: "12px",
            justifyContent: "center",
            flexWrap: "wrap",
          }}
        >
          <button
            type="button"
            onClick={() => navigate("/opening-balance")}
            style={{
              background: "#666",
              color: "white",
              border: "none",
              borderRadius: "8px",
              padding: "10px 18px",
              cursor: "pointer",
              fontSize: "14px",
            }}
          >
            {t('openingBalances.form.cancel')}
          </button>
          <button
            type="submit"
            disabled={isLoading}
            style={{
              backgroundColor: "#AC2000",
              color: "white",
              border: "none",
              borderRadius: "8px",
              padding: "10px 18px",
              cursor: "pointer",
              fontSize: "14px",
              opacity: isLoading ? 0.7 : 1,
            }}
          >
            {isLoading ? t('openingBalances.form.saving') : t('openingBalances.form.save')}
          </button>
        </div>
      </form>
    </div>
  );
};

const labelStyle = {
  display: "block",
  marginBottom: "6px",
  fontSize: "13px",
  color: "#cbd5f5",
};

const inputStyle = {
  width: "100%",
  backgroundColor: "#1a1f2e",
  border: "1px solid #333",
  color: "white",
  borderRadius: "8px",
  padding: "10px",
  boxSizing: "border-box",
};

export default AddOpeningBalance;


